import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import { Role, AccountRole } from '@/types/rbac'

export const useAdminRolesStore = defineStore('adminRoles', () => {
  const roles = ref<Role[]>([])
  const loading = ref(false)
  const rolesLoaded = ref(false)

  async function fetchRoles(): Promise<Role[]> {
    if (rolesLoaded.value) {
      return roles.value
    }

    loading.value = true
    try {
      const res = await useAxios<null, Role[]>('GET', '/admin/roles', true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = Role.array().safeParse(res.data)
        if (validated.success) {
          roles.value = validated.data
          rolesLoaded.value = true
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'AdminRolesStore:fetchRoles' })
    } finally {
      loading.value = false
    }
    return []
  }

  async function fetchAccountRoles(accountId: number): Promise<AccountRole[]> {
    loading.value = true
    try {
      const res = await useAxios<null, AccountRole[]>(
        'GET',
        `/admin/account/${accountId}/roles`,
        true
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRole.array().safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'AdminRolesStore:fetchAccountRoles' })
    } finally {
      loading.value = false
    }
    return []
  }

  async function assignRole(
    accountId: number,
    roleId: number,
    expiresAt?: string
  ): Promise<boolean> {
    loading.value = true
    try {
      const payload = { role_id: roleId, expires_at: expiresAt || null }
      const res = await useAxios<typeof payload, void>(
        'POST',
        `/admin/account/${accountId}/roles`,
        true,
        payload
      )
      return res?.status === STATUS_CODES.OK || res?.status === STATUS_CODES.CREATED
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'AdminRolesStore:assignRole' })
      return false
    } finally {
      loading.value = false
    }
  }

  async function revokeRole(accountId: number, roleId: number): Promise<boolean> {
    loading.value = true
    try {
      const res = await useAxios<null, void>(
        'DELETE',
        `/admin/account/${accountId}/roles/${roleId}`,
        true
      )
      return res?.status === STATUS_CODES.OK || res?.status === STATUS_CODES.NO_CONTENT
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'AdminRolesStore:revokeRole' })
      return false
    } finally {
      loading.value = false
    }
  }

  function invalidateRoles() {
    rolesLoaded.value = false
  }

  return {
    roles,
    loading,
    fetchRoles,
    fetchAccountRoles,
    assignRole,
    revokeRole,
    invalidateRoles
  }
})
